import React, { useState, useEffect, useMemo } from 'react';
import { 
  UploadCloud, Download, Trash2, Search, CheckCircle, 
  AlertCircle, Loader2, Moon, Sun, Plus, X, Database, Filter, 
  ChevronLeft, ChevronRight, ShieldCheck, Tag, DollarSign, MapPin, Package
} from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000'}/api`;

const CATEGORY_CHOICES = [
  { value: 'vehicles', label: 'Cars & Trucks' },
  { value: 'heavy_equipment', label: 'Heavy Equipment & Machinery' },
  { value: 'height_poles', label: 'Height Poles' },
  { value: 'lights_strobes', label: 'Lights & Strobes' },
  { value: 'flags_safety', label: 'Flags & Safety Gear' },
  { value: 'oversize_signs', label: 'Oversize Signs' },
  { value: 'cb_radios_comm', label: 'CB Radios & Comm' },
  { value: 'roof_racks_mounts', label: 'Roof Racks & Mounts' },
  { value: 'complete_kits', label: 'Complete Starter Kits' },
  { value: 'bundles', label: 'Bundles' },
  { value: 'personal_items', label: 'Personal & Household Items' },
  { value: 'other', label: 'Other Items' }
];

const CONDITION_CHOICES = [
  'Brand New', 'Open Box', 'Like New', 'Excellent', 'Good', 'Fair', 'Needs Work', 'For Parts', 'Salvage / Parts Only'
];

const TradeLinkItemsManager = () => {
  // --- Global Theme State ---
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) return savedTheme;
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  });

  // --- Core States ---
  const [listings, setListings] = useState([]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // --- Selection & Modal States ---
  const [selectedIds, setSelectedIds] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newListing, setNewListing] = useState({
    title: '', description: '', price: '', category: 'other', condition: 'Good',
    location: '', zipcode: '', shipping: '', negotiable: false, is_ppca_verified: false,
    compatible_states: '', escort_types: ''
  });

  // --- Pagination & Filtering States ---
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('All');

  // Helper for Authorization Headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // --- Effects ---
  useEffect(() => {
    fetchListings();
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(theme === 'light' ? 'dark' : 'light');

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 5000);
  };

  // --- API Calls ---
  const fetchListings = async () => {
    try {
      const response = await fetch(`${API_BASE}/tradelink/listings/?my=true`, {
        headers: { ...getAuthHeaders() }
      });
      if (response.ok) {
        const data = await response.json();
        // Handle pagination or array response
        setListings(Array.isArray(data) ? data : data.results || []);
      }
    } catch (error) {
      showNotification('Failed to fetch listings. Please check server connection.', 'error');
    }
  };

  // --- Filtering & Pagination Logic ---
  const filteredListings = useMemo(() => {
    return listings.filter(item => {
      const matchesSearch = item.title?.toLowerCase().includes(search.toLowerCase()) || 
                            item.location?.toLowerCase().includes(search.toLowerCase()) ||
                            item.zipcode?.includes(search);
      const matchesCategory = selectedCategoryFilter === 'All' || item.category === selectedCategoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [listings, search, selectedCategoryFilter]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
  }, [search, selectedCategoryFilter, rowsPerPage]);

  const totalPages = rowsPerPage === 'All' ? 1 : Math.ceil(filteredListings.length / rowsPerPage);
  
  const currentData = useMemo(() => {
    if (rowsPerPage === 'All') return filteredListings;
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredListings.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredListings, currentPage, rowsPerPage]);

  // --- Selection Logic ---
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(currentData.map(item => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };

  // --- CRUD Operations ---
  const handleAddSingle = async (e) => {
    e.preventDefault();
    try {
      const formattedStates = newListing.compatible_states.split(',').map(s => s.trim()).filter(Boolean);
      const formattedEscorts = newListing.escort_types.split(',').map(e => e.trim()).filter(Boolean);

      const payload = {
        ...newListing,
        price: parseFloat(newListing.price) || 0,
        compatible_states: formattedStates,
        escort_types: formattedEscorts
      };

      const response = await fetch(`${API_BASE}/tradelink/listings/create/`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...getAuthHeaders() 
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        showNotification('Listing created successfully!', 'success');
        setIsAddModalOpen(false);
        setNewListing({
          title: '', description: '', price: '', category: 'other', condition: 'Good',
          location: '', zipcode: '', shipping: '', negotiable: false, is_ppca_verified: false,
          compatible_states: '', escort_types: ''
        });
        fetchListings();
      } else {
        const err = await response.json();
        showNotification(err.detail || 'Failed to create listing.', 'error');
      }
    } catch (error) {
      showNotification('Network error while adding listing.', 'error');
    }
  };

  // --- Bulk Import ---
  const handleImport = async (e) => {
    e.preventDefault();
    if (!file) return showNotification('Please select a CSV file first.', 'error');
    setLoading(true);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_BASE}/tradelink/listings/bulk-import/`, { 
        method: 'POST', 
        headers: { ...getAuthHeaders() },
        body: formData 
      });
      
      const data = await response.json();
      if (response.ok) {
        showNotification(data.message || 'Listings imported successfully!', 'success');
        setFile(null);
        if (document.getElementById('file-upload')) {
          document.getElementById('file-upload').value = '';
        }
        fetchListings();
      } else {
        showNotification(data.error || data.detail || 'Import failed.', 'error');
      }
    } catch (error) {
      showNotification('Network error during bulk import.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // --- Bulk Export with Authentication Token ---
  const handleExport = async () => {
    try {
      const response = await fetch(`${API_BASE}/tradelink/listings/bulk-export/`, {
        method: 'GET',
        headers: { ...getAuthHeaders() }
      });

      if (!response.ok) {
        throw new Error('Export authorization failed or server error.');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'tradelink_master_products.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      showNotification('Export downloaded successfully!', 'success');
    } catch (error) {
      showNotification(error.message || 'Export failed.', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      const response = await fetch(`${API_BASE}/tradelink/listings/${id}/`, { 
        method: 'DELETE',
        headers: { ...getAuthHeaders() }
      });
      if (response.ok) {
        setListings(listings.filter((item) => item.id !== id));
        setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
        showNotification('Listing deleted.', 'success');
      }
    } catch (error) {
      showNotification('Error deleting listing.', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} selected listings?`)) return;
    setLoading(true);
    try {
      await Promise.all(
        selectedIds.map(id => 
          fetch(`${API_BASE}/tradelink/listings/${id}/`, { 
            method: 'DELETE',
            headers: { ...getAuthHeaders() }
          })
        )
      );
      setListings(listings.filter(item => !selectedIds.includes(item.id)));
      setSelectedIds([]);
      showNotification(`Successfully deleted ${selectedIds.length} listings.`, 'success');
    } catch (error) {
      showNotification('Error deleting selected items.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6 md:p-12 font-sans text-slate-800 dark:text-slate-100 transition-colors duration-200">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
              <Package className="text-blue-600 dark:text-blue-400" size={32} />
              TRADELINK BULK LISTINGS MANAGER
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Manage marketplace inventory, bulk import, and export listings.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={toggleTheme} className="p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all shadow-sm">
              {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            </button>
            <button onClick={() => setIsAddModalOpen(true)} className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-all shadow-sm">
              <Plus size={18} /> New Listing
            </button>
            <button onClick={handleExport} className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 px-5 py-2.5 rounded-lg font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-sm">
              <Download size={18} /> Export CSV
            </button>
          </div>
        </header>

        {/* Notifications */}
        {notification.show && (
          <div className={`flex items-center gap-3 p-4 rounded-lg border ${notification.type === 'success' ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300' : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'}`}>
            {notification.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            <p className="font-medium">{notification.message}</p>
          </div>
        )}

        {/* Import Section */}
        <section className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 transition-colors">
          <form onSubmit={handleImport} className="flex flex-col md:flex-row gap-4 items-center">
            <div className="flex-grow w-full">
              <label htmlFor="file-upload" className={`flex items-center justify-center w-full h-14 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${file ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700'}`}>
                <UploadCloud className={`w-5 h-5 mr-3 ${file ? 'text-blue-500' : 'text-slate-400'}`} />
                <span className="text-sm text-slate-600 dark:text-slate-300 font-medium">
                  {file ? file.name : "Select or Drop tradelink_master_products.csv for Bulk Upload"}
                </span>
                <input id="file-upload" type="file" accept=".csv" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
              </label>
            </div>
            <button 
              type="submit" 
              disabled={!file || loading} 
              className="w-full md:w-auto flex items-center justify-center gap-2 px-8 h-14 rounded-xl font-semibold transition-all duration-300 ease-out text-white bg-green-600 shadow-md hover:bg-green-500 hover:-translate-y-0.5 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {loading ? <Loader2 size={20} className="animate-spin" /> : 'Bulk Import CSV'}
            </button>
          </form>
        </section>

        {/* Database Table Section */}
        <section className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col transition-colors">
          
          {/* Table Toolbar */}
          <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex flex-col lg:flex-row justify-between items-center gap-4 bg-slate-50/50 dark:bg-slate-800/50">
            
            <div className="flex flex-wrap items-center gap-4 w-full lg:w-auto">
              {selectedIds.length > 0 ? (
                <button onClick={handleBulkDelete} className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 font-medium px-4 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 transition">
                  <Trash2 size={16} /> Delete Selected ({selectedIds.length})
                </button>
              ) : (
                <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-medium">
                  <Database size={20} className="text-blue-500" />
                  <span>{filteredListings.length} Total Listings</span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
              {/* Category Filter */}
              <div className="relative flex items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 w-full sm:w-auto">
                <Filter size={16} className="text-slate-400 mr-2" />
                <select 
                  value={selectedCategoryFilter} 
                  onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                  className="bg-transparent text-sm text-slate-700 dark:text-slate-200 outline-none w-full cursor-pointer"
                >
                  <option value="All" className="bg-white dark:bg-slate-800">All Categories</option>
                  {CATEGORY_CHOICES.map(cat => (
                    <option key={cat.value} value={cat.value} className="bg-white dark:bg-slate-800">
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Search Box */}
              <div className="relative w-full sm:w-64">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search title, zip, location..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Listings Table */}
          <div className="overflow-x-auto overflow-y-auto max-h-[550px]">
            <table className="w-full text-left border-collapse relative">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-100 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 text-xs uppercase tracking-wider font-semibold shadow-sm">
                  <th className="px-6 py-4 w-12 text-center bg-slate-100 dark:bg-slate-700">
                    <input 
                      type="checkbox" 
                      onChange={handleSelectAll} 
                      checked={currentData.length > 0 && selectedIds.length === currentData.length}
                      className="rounded border-slate-300 dark:border-slate-500 cursor-pointer w-4 h-4 text-blue-600" 
                    />
                  </th>
                  <th className="px-6 py-4 bg-slate-100 dark:bg-slate-700">Listing</th>
                  <th className="px-4 py-4 bg-slate-100 dark:bg-slate-700">Category</th>
                  <th className="px-4 py-4 bg-slate-100 dark:bg-slate-700">Price</th>
                  <th className="px-4 py-4 bg-slate-100 dark:bg-slate-700">Condition</th>
                  <th className="px-4 py-4 bg-slate-100 dark:bg-slate-700">Location</th>
                  <th className="px-4 py-4 bg-slate-100 dark:bg-slate-700 text-center">Badges</th>
                  <th className="px-6 py-4 text-right bg-slate-100 dark:bg-slate-700">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {currentData.length > 0 ? (
                  currentData.map((item) => (
                    <tr key={item.id} className="hover:bg-blue-50/50 dark:hover:bg-slate-700/50 transition-colors group">
                      <td className="px-6 py-4 text-center">
                        <input 
                          type="checkbox" 
                          checked={selectedIds.includes(item.id)}
                          onChange={() => handleSelectRow(item.id)}
                          className="rounded border-slate-300 dark:border-slate-500 cursor-pointer w-4 h-4 text-blue-600" 
                        />
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-semibold text-slate-900 dark:text-white">{item.title}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">{item.description || 'No description provided.'}</p>
                      </td>
                      <td className="px-4 py-4 text-xs font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">
                        <span className="px-2.5 py-1 rounded-md bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                          {CATEGORY_CHOICES.find(c => c.value === item.category)?.label || item.category}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                        ${parseFloat(item.price).toFixed(2)}
                      </td>
                      <td className="px-4 py-4 text-xs font-medium whitespace-nowrap">
                        <span className="px-2 py-0.5 rounded text-slate-700 dark:text-slate-200 bg-slate-200 dark:bg-slate-700">
                          {item.condition}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <MapPin size={12} className="text-slate-400" />
                          <span>{item.location || 'N/A'} {item.zipcode ? `(${item.zipcode})` : ''}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center whitespace-nowrap space-x-1">
                        {item.is_ppca_verified && (
                          <span className="inline-inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                            <ShieldCheck size={10} /> PPCA
                          </span>
                        )}
                        {item.negotiable && (
                          <span className="inline-inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                            Negotiable
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        <button onClick={() => handleDelete(item.id)} className="text-slate-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 dark:hover:bg-slate-700 transition">
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="px-6 py-16 text-center text-slate-500 dark:text-slate-400">
                      No TradeLink listings found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row justify-between items-center gap-4 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex items-center text-sm text-slate-600 dark:text-slate-400">
              <span className="mr-2">Rows per page:</span>
              <select 
                value={rowsPerPage} 
                onChange={(e) => setRowsPerPage(e.target.value === 'All' ? 'All' : Number(e.target.value))}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              >
                {[10, 25, 50, 100, 'All'].map(val => (
                  <option key={val} value={val} className="bg-white dark:bg-slate-800">{val}</option>
                ))}
              </select>
            </div>

            {rowsPerPage !== 'All' && totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 transition"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300 px-3">
                  Page {currentPage} of {totalPages}
                </span>
                <button 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 transition"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </div>
        </section>

      </div>

      {/* Add Listing Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200 dark:border-slate-700 my-8">
            <div className="flex justify-between items-center p-5 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">Create TradeLink Listing</h3>
              <button onClick={() => setIsAddModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleAddSingle} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* Title */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Title</label>
                  <input required type="text" value={newListing.title} onChange={e => setNewListing({...newListing, title: e.target.value})} className="w-full px-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-blue-500" placeholder="Item Title..." />
                </div>

                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Price ($)</label>
                  <input required type="number" step="0.01" value={newListing.price} onChange={e => setNewListing({...newListing, price: e.target.value})} className="w-full px-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-blue-500" placeholder="0.00" />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Category</label>
                  <select value={newListing.category} onChange={e => setNewListing({...newListing, category: e.target.value})} className="w-full px-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-blue-500">
                    {CATEGORY_CHOICES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                {/* Condition */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Condition</label>
                  <select value={newListing.condition} onChange={e => setNewListing({...newListing, condition: e.target.value})} className="w-full px-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-blue-500">
                    {CONDITION_CHOICES.map(cond => (
                      <option key={cond} value={cond}>{cond}</option>
                    ))}
                  </select>
                </div>

                {/* Shipping */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Shipping Info</label>
                  <input type="text" value={newListing.shipping} onChange={e => setNewListing({...newListing, shipping: e.target.value})} className="w-full px-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-blue-500" placeholder="e.g. Free Shipping / Local Pickup" />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Location</label>
                  <input type="text" value={newListing.location} onChange={e => setNewListing({...newListing, location: e.target.value})} className="w-full px-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-blue-500" placeholder="City, State" />
                </div>

                {/* Zipcode */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Zipcode</label>
                  <input type="text" value={newListing.zipcode} onChange={e => setNewListing({...newListing, zipcode: e.target.value})} className="w-full px-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-blue-500" placeholder="10001" />
                </div>

                {/* Compatible States */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">States (comma-separated)</label>
                  <input type="text" value={newListing.compatible_states} onChange={e => setNewListing({...newListing, compatible_states: e.target.value})} className="w-full px-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-blue-500" placeholder="NY, CA, TX" />
                </div>

                {/* Escort Types */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Escort Types (comma-separated)</label>
                  <input type="text" value={newListing.escort_types} onChange={e => setNewListing({...newListing, escort_types: e.target.value})} className="w-full px-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-blue-500" placeholder="Lead, Chase, Pole" />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Description</label>
                  <textarea rows="3" value={newListing.description} onChange={e => setNewListing({...newListing, description: e.target.value})} className="w-full px-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-blue-500 resize-none" placeholder="Enter listing details..." />
                </div>

                {/* Checkboxes */}
                <div className="md:col-span-2 flex items-center gap-6 pt-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-slate-700 dark:text-slate-300">
                    <input type="checkbox" checked={newListing.negotiable} onChange={e => setNewListing({...newListing, negotiable: e.target.checked})} className="rounded text-blue-600 w-4 h-4" />
                    Negotiable Price
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-slate-700 dark:text-slate-300">
                    <input type="checkbox" checked={newListing.is_ppca_verified} onChange={e => setNewListing({...newListing, is_ppca_verified: e.target.checked})} className="rounded text-blue-600 w-4 h-4" />
                    PPCA Verified
                  </label>
                </div>

              </div>

              <div className="pt-4 flex justify-end gap-3 border-t border-slate-100 dark:border-slate-700 mt-6">
                <button type="button" onClick={() => setIsAddModalOpen(false)} className="px-5 py-2 text-sm font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-lg transition-colors">
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-sm">
                  Save Listing
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeLinkItemsManager;